import { Heart, Briefcase, Building, Rocket, TrendingUp, Home, ShoppingBag, Shield, Plane, Truck } from 'lucide-react';

export const industries = [
  {
    id: 'healthcare',
    slug: 'healthcare',
    title: 'Healthcare & Medical Organisations',
    shortTitle: 'Healthcare',
    tagline: 'Supporting care through operational excellence',
    icon: Heart,
    color: '#0E5A57',
    description: 'Healthcare organisations trust NKP VENTURES to manage the administrative and operational burden that pulls clinical teams away from patient care.',
    heroSub: 'Administrative excellence for organisations that put patient care first.',
    painPoints: [
      'Clinical staff spending 35-40% of time on administrative tasks',
      'Patient communication backlogs affecting satisfaction and retention',
      'Documentation gaps creating compliance and audit risk',
      'High administrative overhead reducing investment in patient care',
      'Difficulty maintaining consistent processes as the organisation scales',
    ],
    supportAreas: [
      'Patient communication and appointment administration',
      'Medical documentation and records management',
      'Billing and financial administration support',
      'Back-office data processing and form management',
      'Compliance documentation management',
      'Operational reporting and performance tracking',
    ],
    whyPkepl: 'NKP VENTURES brings healthcare-sector awareness, strict confidentiality standards, and documented operational processes to every healthcare engagement. Our long-term partnership with a UK-based healthcare organisation demonstrates our ability to embed as a trusted operational extension of healthcare teams.',
    governanceNote: 'All healthcare engagements operate under comprehensive confidentiality agreements, healthcare-appropriate data handling protocols, and structured quality assurance processes.',
    seoTitle: 'Healthcare Administration BPO | NKP VENTURES',
    seoDesc: 'Specialist administrative and operational support for healthcare organisations. Patient communication, records management, and back-office operations.',
  },
  {
    id: 'professional-services',
    slug: 'professional-services',
    title: 'Professional Services',
    shortTitle: 'Professional Services',
    tagline: 'Operational support for firms that run on expertise',
    icon: Briefcase,
    color: '#2E7D7A',
    description: 'Legal, accounting, and consulting firms leverage NKP VENTURES to manage the operational and administrative complexity that grows with their practices.',
    heroSub: 'Let your professionals focus on expertise. We manage the operations.',
    painPoints: [
      'Professionals spending billable hours on non-billable administrative tasks',
      'Client onboarding processes that are slow and inconsistent',
      'Document management creating version control and compliance issues',
      'Difficulty scaling operational support as the practice grows',
      'High cost of administrative staff in professional services contexts',
    ],
    supportAreas: [
      'Client onboarding administration',
      'Document preparation and management',
      'Billing support and invoice processing',
      'Research and due diligence support',
      'Scheduling and client communication',
      'CRM data management and maintenance',
    ],
    whyPkepl: 'NKP VENTURES understands that professional services firms run on reputation, confidentiality, and operational precision. We bring the same standards to our support role, operating with the discretion and professionalism that your clients would expect from your own team.',
    governanceNote: 'All engagements are governed by comprehensive NDAs and confidentiality agreements. Data is handled with professional services-appropriate standards of discretion.',
    seoTitle: 'BPO for Professional Services Firms | NKP VENTURES',
    seoDesc: 'Administrative and operational support for legal, accounting, and consulting firms. Client administration, document management, and billing support.',
  },
  {
    id: 'smes',
    slug: 'smes',
    title: 'Small & Medium Enterprises',
    shortTitle: 'SMEs',
    tagline: 'Enterprise-grade operations for growing businesses',
    icon: Building,
    color: '#153E5C',
    description: 'SMEs gain access to enterprise-quality operational support without enterprise-level overheads, enabling them to compete effectively in international markets.',
    heroSub: 'The operational infrastructure that lets SMEs compete at the next level.',
    painPoints: [
      'Founders and directors handling operational tasks that distract from growth',
      'Cannot justify full-time hires for every operational function',
      'Inconsistent processes creating quality and reliability issues',
      'Difficulty accessing specialist operational skills within budget',
      'Growth creating operational complexity faster than capacity can be added',
    ],
    supportAreas: [
      'Administrative and operational support',
      'Customer support and client communication',
      'Finance and bookkeeping support',
      'HR administration',
      'Marketing and business development support',
      'General back-office operations',
    ],
    whyPkepl: 'NKP VENTURES was built to serve businesses that need professional operational support without the cost and complexity of building large internal teams. We provide SMEs with the operational quality, consistency, and scalability that were previously only accessible to larger enterprises.',
    governanceNote: 'We operate as a flexible, long-term operational partner — scaling support as your business grows without the delays and costs of internal hiring.',
    seoTitle: 'BPO for Small & Medium Businesses | NKP VENTURES',
    seoDesc: 'Enterprise-grade operational support for SMEs. Administrative, customer support, finance, and back-office services.',
  },
  {
    id: 'startups',
    slug: 'startups',
    title: 'Startups & High-Growth Businesses',
    shortTitle: 'Startups',
    tagline: 'Operational scale without operational overhead',
    icon: Rocket,
    color: '#0E5A57',
    description: 'Fast-growing businesses need operational infrastructure that scales with them. NKP VENTURES provides the support layer that lets startups move fast without breaking their operations.',
    heroSub: 'Scale your operations without scaling your headcount.',
    painPoints: [
      'Rapid growth creating operational chaos and coordination failures',
      'Founders spending time on operations instead of product and market',
      'Inability to hire quickly enough to support growth',
      'Inconsistent customer experience as team scales',
      'Operational technical debt accumulating faster than it can be addressed',
    ],
    supportAreas: [
      'Customer support and user communication',
      'Administrative operations',
      'Data management and processing',
      'Virtual assistance for founders and leadership',
      'Business research and market intelligence',
      'Back-office operations',
    ],
    whyPkepl: 'NKP VENTURES understands the pace and pressure of high-growth environments. We build operational infrastructure that scales with you — flexible, responsive, and designed to grow as quickly as your business does.',
    governanceNote: 'We move at startup speed. Engagements can be initiated quickly and expanded rapidly as your operational needs evolve.',
    seoTitle: 'BPO for Startups & High-Growth Companies | NKP VENTURES',
    seoDesc: 'Operational support for fast-growing startups. Customer support, admin, and back-office services that scale with your growth.',
  },
  {
    id: 'financial-services',
    slug: 'financial-services',
    title: 'Financial Services & FinTech',
    shortTitle: 'Financial Services',
    tagline: 'Operations-grade support for regulated businesses',
    icon: TrendingUp,
    color: '#2E7D7A',
    description: 'Financial services firms and FinTech businesses require operational support that matches their precision, compliance orientation, and professional standards.',
    heroSub: 'Precision operational support for businesses where accuracy is non-negotiable.',
    painPoints: [
      'Compliance documentation requirements consuming operational capacity',
      'Data accuracy requirements exceeding what manual processes can consistently deliver',
      'Client onboarding and KYC administration creating bottlenecks',
      'Regulatory reporting demands outpacing internal administrative capacity',
      'High cost of financially-literate operational staff',
    ],
    supportAreas: [
      'Financial data entry and processing',
      'Compliance documentation support',
      'Client onboarding administration',
      'Reporting and analytics preparation',
      'Back-office financial operations',
      'Document management and archiving',
    ],
    whyPkepl: 'NKP VENTURES brings the accuracy standards, confidentiality protocols, and process discipline that financial services businesses require. Our structured quality controls and documented processes are designed to meet the precision requirements of regulated environments.',
    governanceNote: 'All financial services engagements operate under enhanced confidentiality protocols, accuracy standards, and data handling procedures appropriate to the sector.',
    seoTitle: 'BPO for Financial Services & FinTech | NKP VENTURES',
    seoDesc: 'Compliance-aware operational support for financial services firms and FinTech companies. Data processing, client administration, and reporting.',
  },
  {
    id: 'real-estate',
    slug: 'real-estate',
    title: 'Real Estate & Property Management',
    shortTitle: 'Real Estate',
    tagline: 'Keeping your properties and operations in order',
    icon: Home,
    color: '#153E5C',
    description: 'Real estate and property management businesses depend on consistent operations, reliable communication, and accurate documentation. NKP VENTURES provides the support infrastructure to deliver these consistently.',
    heroSub: 'Operational support that keeps your portfolio running smoothly.',
    painPoints: [
      'High volume of administrative tasks across a large property portfolio',
      'Tenant communication backlogs affecting satisfaction and retention',
      'Maintenance coordination creating operational inefficiencies',
      'Lease and contract documentation management consuming staff time',
      'Inconsistent communication quality across different properties',
    ],
    supportAreas: [
      'Tenant communication and correspondence',
      'Lease administration and documentation',
      'Maintenance request coordination',
      'Property listing support',
      'Landlord and owner communication',
      'Data management and reporting',
    ],
    whyPkepl: 'NKP VENTURES understands the volume-intensive, documentation-heavy nature of property management operations. Our structured processes, reliable communication standards, and scalable capacity make us a strong operational partner for property businesses of all sizes.',
    governanceNote: 'Property and tenancy data is handled with strict confidentiality and access controls appropriate to the sector.',
    seoTitle: 'BPO for Real Estate & Property Management | NKP VENTURES',
    seoDesc: 'Administrative and operational support for real estate firms. Tenant communication, lease administration, and property management support.',
  },
  {
    id: 'ecommerce-retail',
    slug: 'ecommerce-retail',
    title: 'E-commerce & Retail',
    shortTitle: 'E-commerce & Retail',
    tagline: 'Operational excellence behind every customer interaction',
    icon: ShoppingBag,
    color: '#0E5A57',
    description: 'E-commerce and retail businesses need operational support that scales with their volume, maintains their brand standards, and keeps customers satisfied.',
    heroSub: 'Scale your e-commerce operations without scaling your team.',
    painPoints: [
      'Customer support volumes overwhelming internal teams',
      'Order processing and back-office operations struggling with peak demand',
      'Returns and dispute management creating operational backlogs',
      'Product data management becoming a bottleneck for catalogue growth',
      'Inconsistent customer communication affecting brand reputation',
    ],
    supportAreas: [
      'Customer support and inquiry management',
      'Order processing and tracking support',
      'Returns and dispute management',
      'Product data entry and catalogue management',
      'Review management and response',
      'Back-office e-commerce operations',
    ],
    whyPkepl: 'NKP VENTURES has direct experience building and operating e-commerce businesses through our HYPPE AROMA venture. We understand the operational pressures of running an online retail business — from customer experience to operational efficiency — from the inside.',
    governanceNote: 'We scale with your business through peak seasons and growth phases without the delays and costs of internal hiring.',
    seoTitle: 'BPO for E-commerce & Retail | NKP VENTURES',
    seoDesc: 'Customer support, order processing, and back-office operations for e-commerce and retail businesses.',
  },
  {
    id: 'insurance',
    slug: 'insurance',
    title: 'Insurance',
    shortTitle: 'Insurance',
    tagline: 'Precision operations for complex processes',
    icon: Shield,
    color: '#2E7D7A',
    description: 'Insurance businesses require operational support that matches the precision, documentation standards, and regulatory awareness of their sector.',
    heroSub: 'Accurate, compliant operational support for insurance businesses.',
    painPoints: [
      'Claims administration consuming significant operational capacity',
      'Policy documentation management creating version control issues',
      'Customer communication requiring consistent professional standards',
      'Compliance documentation demands exceeding internal capacity',
      'High administrative overhead in a margin-sensitive business',
    ],
    supportAreas: [
      'Policy administration support',
      'Claims processing and documentation',
      'Customer communication and correspondence',
      'Data entry and records management',
      'Compliance documentation support',
      'Reporting and operational analytics',
    ],
    whyPkepl: 'NKP VENTURES brings the precision, documentation discipline, and confidentiality standards that insurance operations require. Our structured processes and quality controls are designed to meet the accuracy requirements of regulated insurance environments.',
    governanceNote: 'Insurance data and documentation is handled under enhanced confidentiality protocols with role-based access and comprehensive audit trails.',
    seoTitle: 'BPO for Insurance Companies | NKP VENTURES',
    seoDesc: 'Claims administration, policy documentation, and back-office support for insurance businesses.',
  },
  {
    id: 'hospitality-travel',
    slug: 'hospitality-travel',
    title: 'Hospitality & Travel',
    shortTitle: 'Hospitality & Travel',
    tagline: 'Consistent experiences through operational excellence',
    icon: Plane,
    color: '#153E5C',
    description: 'Hospitality and travel businesses depend on flawless operational execution across every customer touchpoint. NKP VENTURES provides the behind-the-scenes support that makes this possible.',
    heroSub: 'Behind every great guest experience is exceptional operations.',
    painPoints: [
      'Reservation and booking administration creating operational backlogs',
      'Guest communication inconsistency affecting experience quality',
      'Seasonal volume spikes overwhelming internal capacity',
      'Supplier and vendor coordination consuming management time',
      'Review management and reputation monitoring not being done systematically',
    ],
    supportAreas: [
      'Reservation and booking administration',
      'Guest communication and correspondence',
      'Supplier and vendor coordination',
      'Review management and response',
      'Back-office operations and reporting',
      'Customer data management',
    ],
    whyPkepl: 'NKP VENTURES understands that in hospitality and travel, the operational details are the guest experience. We apply the same standards of professionalism, consistency, and attention to detail to our operational support that your guests expect from your service.',
    governanceNote: 'Guest and booking data is handled with strict confidentiality and privacy standards appropriate to the hospitality sector.',
    seoTitle: 'BPO for Hospitality & Travel | NKP VENTURES',
    seoDesc: 'Guest communication, reservation management, and back-office operations for hospitality and travel businesses.',
  },
  {
    id: 'logistics',
    slug: 'logistics',
    title: 'Logistics & Supply Chain',
    shortTitle: 'Logistics & Supply Chain',
    tagline: 'Operational precision for complex supply chains',
    icon: Truck,
    color: '#0E5A57',
    description: 'Logistics and supply chain businesses need operational support that matches their precision requirements, volume demands, and coordination complexity.',
    heroSub: 'Operational support for businesses that cannot afford mistakes.',
    painPoints: [
      'Order processing and documentation volumes overwhelming operational teams',
      'Supplier communication and coordination creating delays',
      'Data accuracy requirements in tracking and reporting exceeding manual capacity',
      'Compliance documentation demands growing with regulatory complexity',
      'Customer communication backlogs during peak operational periods',
    ],
    supportAreas: [
      'Order processing and documentation',
      'Supplier and vendor communication',
      'Shipment tracking and coordination support',
      'Data entry and processing',
      'Compliance documentation management',
      'Customer communication and inquiry management',
    ],
    whyPkepl: 'NKP VENTURES brings the accuracy standards, process discipline, and scalable capacity that logistics and supply chain operations require. Our structured quality controls ensure that operational support matches the precision your supply chain demands.',
    governanceNote: 'Supply chain data and documentation is handled under comprehensive confidentiality protocols with appropriate data security standards.',
    seoTitle: 'BPO for Logistics & Supply Chain | NKP VENTURES',
    seoDesc: 'Order processing, supplier communication, and back-office operations for logistics and supply chain businesses.',
  },
];

export const getIndustryBySlug = (slug) => industries.find(i => i.slug === slug);
